import { CallReason } from "@closerplatform/closer-sdk";
import {
  AgentProfile,
  ConversationStatus,
  WidgetFormSubmissionWithWidget
} from "@closerplatform/spinner-openapi";
import { action, observable } from "mobx";
import { UserProfile } from "../userProfile";
import { MeetingStatus } from "@fidget/common/models/meeting-status";
import { MeetingSchedulerStatus } from "@fidget/common/models/meeting-scheduler-status";

export enum ChatItemStatus {
  SENDING = "Sending",
  SENT = "Sent",
  DELIVERED = "Delivered",
  READ = "Read",
  UNDELIVERED = "Undelivered"
}

export interface IAttachment {
  token: string;
  id?: string;
  roomId?: string;
  contentType?: string;
  fileName?: string;
  previews: Array<string>;
  size?: number;
  metadata?: any;
}

export interface IChatItem {
  readonly id: string;
  readonly tag: string;
  readonly timestamp: number;
}

export interface IUserChatItem {
  readonly user: UserProfile;
}

export interface ITransferableChatItem {
  readonly status: ChatItemStatus;
}

export interface ITextChatItem {
  readonly text: string;
}

export interface IAttachmentsChatItem {
  readonly attachments?: IAttachment[];
}

export interface CallAnsweredPayload {
  timestamp: number;
}

export interface CallEndedPayload {
  duration: number;
  userId: string;
  reason: CallReason;
}

export interface CobrowsingEndedPayload {
  duration: number;
}

export interface CobrowsingStartedPayload {
  timestamp: number;
}

export interface FormPayload {
  formId: string;
}

export interface LeadTagsUpdatePayload {
  oldTags: string[];
  oldTagGroupId?: string;
  tags: string[];
  tagGroupId?: string;
  unknownTags?: string[];
  matchingSteps?: Array<"RequiredTagsMatched" | "MaximumCountOfNonRequiredTagsSelected" | "MinimumCountOfTagsSelected" | "RandomSelected">;
}

export interface LeadTopicsUpdatePayload {
  topics: string[];
}

export interface IGroupableItem extends IChatItem, IUserChatItem, ITransferableChatItem { }

export const isGroupableItem = (item: IChatItem): item is IGroupableItem =>
  UserTextChatItem.isUserTextChatItem(item) ||
  ContactEnquiryChatItem.isContactEnquiryChatItem(item) ||
  MeetingChatItem.isMeetingChatItem(item) ||
  MeetingSchedulerChatItem.isMeetingSchedulerChatItem(item) ||
  CallProposalChatItem.isCallProposalChatItem(item) ||
  CobrowsingProposalChatItem.isCobrowsingProposalChatItem(item) ||
  MessageWithFormChatItem.isMessageWithFormChatItem(item) ||
  TypingChatItem.isTypingChatItem(item);

export interface IUpdateableChatItem extends IChatItem {
  updateFromEvent: (data: any) => void;
}

export const isUpdateableChatItem = (item: IChatItem): item is IUpdateableChatItem =>
  MeetingSchedulerChatItem.isMeetingSchedulerChatItem(item);

export interface IMessageItem extends IGroupableItem, ITextChatItem, IAttachmentsChatItem { }

abstract class AbstractTextChatItem implements IChatItem, ITextChatItem {
  protected constructor(
    public readonly tag: string,
    public readonly id: string,
    public readonly timestamp: number,
    public readonly text: string,
  ) { }
}

abstract class AbstractUserTextChatItem
  extends AbstractTextChatItem
  implements IMessageItem, IUserChatItem {
  protected constructor(
    tag: string,
    id: string,
    timestamp: number,
    text: string,
    public readonly user: UserProfile,
    public status: ChatItemStatus,
  ) {
    super(tag, id, timestamp, text);
  }
}

export class NotificationChatItem extends AbstractTextChatItem implements IChatItem, ITextChatItem {
  public static readonly tag = "notification-chat-item";

  constructor(id: string, timestamp: number, text: string) {
    super(NotificationChatItem.tag, id, timestamp, text);
  }

  public static isNotificationChatItem = (m: IChatItem): m is NotificationChatItem =>
    m.tag === NotificationChatItem.tag;
}

export class UserTextChatItem extends AbstractUserTextChatItem implements IMessageItem {
  public static readonly tag = "user-text-chat-item";

  constructor(
    id: string,
    timestamp: number,
    text: string,
    user: UserProfile,
    status: ChatItemStatus,
    public readonly attachments?: IAttachment[],
    public readonly context?: unknown,
  ) {
    super(UserTextChatItem.tag, id, timestamp, text, user, status);
  }

  public static isUserTextChatItem = (m: IChatItem): m is UserTextChatItem =>
    m.tag === UserTextChatItem.tag;
}

export class CallProposalChatItem extends AbstractUserTextChatItem implements IMessageItem {
  public static readonly tag = "call-proposal-chat-item";

  constructor(
    id: string,
    timestamp: number,
    text: string,
    user: UserProfile,
    status: ChatItemStatus,
    public readonly attachments?: IAttachment[]
  ) {
    super(CallProposalChatItem.tag, id, timestamp, text, user, status);
  }
  public static isCallProposalChatItem = (m: IChatItem): m is CallProposalChatItem =>
    m.tag === CallProposalChatItem.tag;
}

export class CobrowsingProposalChatItem extends AbstractUserTextChatItem implements IMessageItem {
  public static readonly tag = "cobrowsing-proposal-chat-item";

  constructor(
    id: string,
    timestamp: number,
    text: string,
    user: UserProfile,
    status: ChatItemStatus,
    public readonly attachments?: IAttachment[]
  ) {
    super(CobrowsingProposalChatItem.tag, id, timestamp, text, user, status);
  }
  public static isCobrowsingProposalChatItem = (m: IChatItem): m is CobrowsingProposalChatItem =>
    m.tag === CobrowsingProposalChatItem.tag;
}

export class MessageWithFormChatItem extends AbstractUserTextChatItem implements IMessageItem {
  public static readonly tag = "message-with-form-chat-item";

  public formId: string;
  @observable public submission: WidgetFormSubmissionWithWidget;

  constructor(
    id: string,
    timestamp: number,
    text: string,
    user: UserProfile,
    status: ChatItemStatus,
    formId: string,
    submission: WidgetFormSubmissionWithWidget
  ) {
    super(MessageWithFormChatItem.tag, id, timestamp, text, user, status);
    this.formId = formId;
    this.submission = submission;
  }

  public setSubmission(submission: WidgetFormSubmissionWithWidget) {
    this.submission = submission;
  }

  public static isMessageWithFormChatItem = (m: IChatItem): m is MessageWithFormChatItem =>
    m.tag === MessageWithFormChatItem.tag;
}

export class FakeUserTextChatItem extends AbstractUserTextChatItem implements IMessageItem {
  public static readonly tag = "fake-user-text-chat-item";

  constructor(
    id: string,
    timestamp: number,
    text: string,
    user: UserProfile,
    status: ChatItemStatus,
    public readonly attachments?: IAttachment[]
  ) {
    super(FakeUserTextChatItem.tag, id, timestamp, text, user, status);
  }

  public static isFakeUserTextChatItem = (m: IChatItem): m is FakeUserTextChatItem =>
    m.tag === FakeUserTextChatItem.tag;
}

export class TypingChatItem implements IGroupableItem {
  public static readonly tag = "typing-chat-item";

  public readonly tag = TypingChatItem.tag;

  constructor(public id: string, public user: AgentProfile, public timestamp: number) {}

  public static isTypingChatItem = (m: IChatItem): m is TypingChatItem => m.tag === TypingChatItem.tag;

  get status(): ChatItemStatus {
    return ChatItemStatus.SENT;
  }
}

export class DynamicSuggestionsChatItem implements IChatItem {
  public static readonly tag = "dynamic-suggestions-chat-item";

  public readonly tag = DynamicSuggestionsChatItem.tag;

  constructor(public id: string, public dynamicSuggestions: string[], public timestamp: number) { }

  public static isDynamicSuggestionsChatItem = (m: IChatItem): m is DynamicSuggestionsChatItem =>
    m.tag === DynamicSuggestionsChatItem.tag;
}

// TODO: Check usages and remove on chat items refactor, widget doesn't use notes
export class NoteChatItem
  extends AbstractTextChatItem
  implements IChatItem, IUserChatItem, ITextChatItem {
  public static readonly tag = "note-chat-item";

  constructor(id: string, timestamp: number, text: string, public readonly user: UserProfile) {
    super(NoteChatItem.tag, id, timestamp, text);
  }

  public static isNoteChatItem = (m: IChatItem): m is NoteChatItem => m.tag === NoteChatItem.tag;
}

export class MeetingChatItem implements IChatItem {
  public static readonly tag = "meeting-chat-item";

  public readonly tag = MeetingChatItem.tag;

  @observable public start: number;
  @observable public duration: number;
  @observable public meetingStatus: MeetingStatus;
  @observable public changed: boolean;

  constructor(
    public readonly id: string,
    public readonly timestamp: number,
    start: number,
    duration: number,
    public agent: UserProfile | undefined,
    public user: UserProfile,
    meetingStatus: MeetingStatus,
    changed: boolean
  ) {
    this.start = start;
    this.duration = duration;
    this.meetingStatus = meetingStatus;
    this.changed = changed;
  }

  public static isMeetingChatItem = (m: IChatItem): m is MeetingChatItem =>
    m.tag === MeetingChatItem.tag;

  get status(): ChatItemStatus {
    return ChatItemStatus.SENT;
  }
}

export class MeetingSchedulerChatItem implements IChatItem, IUserChatItem {
  public static readonly tag = "meeting-scheduler-chat-item";

  public readonly tag = MeetingSchedulerChatItem.tag;

  @observable public progressStatus: MeetingSchedulerStatus;
  @observable public meetingId: string | undefined;

  constructor(
    public readonly id: string,
    public readonly timestamp: number,
    progressStatus: MeetingSchedulerStatus,
    public token: string,
    public user: UserProfile,
    meetingId?: string
  ) {
    this.progressStatus = progressStatus;
    this.meetingId = meetingId;
  }

  public static isMeetingSchedulerChatItem = (m: IChatItem): m is MeetingSchedulerChatItem =>
    m.tag === MeetingSchedulerChatItem.tag;

  get status(): ChatItemStatus {
    return ChatItemStatus.SENT;
  }

  @action
  public updateFromEvent = (data: any) => {
    this.progressStatus = data.context.status;
  };
}

export class StatusChangeChatItem implements IChatItem {
  public static readonly tag = "conversation-status-chat-item";

  public readonly tag = StatusChangeChatItem.tag;

  constructor(
    public readonly id: string,
    public readonly timestamp: number,
    public readonly status: ConversationStatus,
    public readonly user?: UserProfile
  ) { }

  public static isStatusChangeChatItem = (m: IChatItem): m is StatusChangeChatItem =>
    m.tag === StatusChangeChatItem.tag;
}

export class DateSeparatorChatItem implements IChatItem {
  public static readonly tag = "date-separator";

  public readonly tag = DateSeparatorChatItem.tag;

  constructor(public readonly id: string, public readonly timestamp: number) { }

  public static isDateSeparatorChatItem = (m: IChatItem): m is DateSeparatorChatItem =>
    m.tag === DateSeparatorChatItem.tag;
}

export class ContactEnquiryChatItem
  extends AbstractTextChatItem
  implements IGroupableItem, ITextChatItem, IUserChatItem {
  public static readonly tag = "contact-enquiry-chat-item";
  public readonly gdprEnabled: boolean;

  constructor(
    id: string,
    timestamp: number,
    text: string,
    gdprEnabled: boolean,
    public readonly user: UserProfile
  ) {
    super(ContactEnquiryChatItem.tag, id, timestamp, text);
    this.gdprEnabled = gdprEnabled;
  }

  get status(): ChatItemStatus {
    return ChatItemStatus.SENT;
  }

  public static isContactEnquiryChatItem = (m: IChatItem): m is ContactEnquiryChatItem =>
    m.tag === ContactEnquiryChatItem.tag;
}

export class EmptyChatItem implements IChatItem {
  public static readonly tag = "empty-chat-item";

  public readonly tag = EmptyChatItem.tag;

  constructor(public readonly id: string, public readonly timestamp: number) { }

  public static isEmptyChatItem = (m: IChatItem): m is EmptyChatItem => m.tag === EmptyChatItem.tag;
}
